"use client";

import { RefObject, useEffect, useRef, useState } from "react";
import BannerComponent from "./BannerComponent";
import OverlayComponent from "./OverlayComponent";
import classNames from "classnames";
import { observer } from "mobx-react";
import { useShouldDisplayHero } from "../../../../hooks/useShouldDisplayHero";

const localStorageKeys = {
  banner: "dismissedBanners",
  overlay: "dismissedOverlays",
  notificationOnly: "dismissedNotification",
};

function safeGetDismissed(key: string): Array<string | number> {
  try {
    return JSON.parse(sessionStorage.getItem(key) as string) || [];
  } catch {
    return [];
  }
}

function safeSetDismissed(key: string, ids: Array<string | number>): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(ids));
  } catch {
    // storage unavailable
  }
}

const NotificationContainer = observer(
  (props: {
    notifications: string;
    shouldShowOverlay: boolean;
    shouldShowBanner: boolean;
  }) => {
    const { notifications = JSON.stringify([]) } = props;

    const overlayNotifications: RefObject<
      Array<{
        type: "banner" | "overlay";
        id: string | number;
        message: string;
      }>
    > = useRef([]);
    const bannerNotifications: RefObject<
      Array<{
        type: "banner" | "overlay";
        id: string | number;
        message: string;
      }>
    > = useRef([]);

    const [shouldShowBanner, setShouldShowBanner] = useState(false);
    const [shouldShowOverlay, setShouldShowOverlay] = useState(false);
    const heroIsEnabled = useShouldDisplayHero();

    useEffect(() => {
      // check local storage for both alert types
      overlayNotifications.current = [];
      bannerNotifications.current = [];

      const n = JSON.parse(notifications);

      let showOverlay = false,
        showBanner = false,
        dismissedIds = {},
        storageKey;

      Object.keys(localStorageKeys).forEach((notificationType) => {
        storageKey = localStorageKeys[notificationType];
        dismissedIds[notificationType] = safeGetDismissed(storageKey);
      });

      n.map((notification) => {
        switch (notification.type) {
          case "overlay":
            overlayNotifications.current.push(notification);
            break;

          case "banner":
            bannerNotifications.current.push(notification);
            break;
        }

        if (
          notification.type &&
          dismissedIds[notification.type].indexOf(notification.id) === -1
        ) {
          if (notification.type === "banner") {
            showBanner = true;
          } else {
            showOverlay = true;
          }
        }
      });

      setShouldShowOverlay(showOverlay);
      setShouldShowBanner(showBanner);
    }, []);

    function handleDismiss(props) {
      const { alerts, type } = props;

      safeSetDismissed(
        localStorageKeys[type],
        alerts.map((alert) => alert.id),
      );

      if (type === "banner") {
        setShouldShowBanner(false);
      } else {
        setShouldShowOverlay(false);
      }
    }

    let overlay, banner;

    if (shouldShowBanner && props.shouldShowBanner) {
      banner = (
        <BannerComponent
          alerts={bannerNotifications.current}
          handleDismiss={handleDismiss}
        />
      );
    }

    if (shouldShowOverlay && props.shouldShowOverlay) {
      overlay = (
        <OverlayComponent
          alerts={overlayNotifications.current}
          handleDismiss={handleDismiss}
        />
      );
    }

    const notificationClassName = classNames({
      "sb-top-drawer-container": true,
      "sb-notification": true,
      open: true,
    });

    const notificationContainerClassName = classNames({
      "notification-container": true,
      "notification-container-with-hero": heroIsEnabled,
      "banner-notification-container":
        shouldShowBanner && props.shouldShowBanner,
      "overlay-notification-container":
        shouldShowOverlay && props.shouldShowOverlay,
    });

    return (
      <div className={notificationContainerClassName}>
        <div
          className={"sb-top-drawer sb-notification-drawer"}
          style={{ visibility: "visible" }}
        >
          <div className={notificationClassName}>
            {banner}
            {overlay}
          </div>
        </div>
      </div>
    );
  },
);

export default NotificationContainer;
